import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { coursesApi, type GradingMode } from '@/api/courses';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { humanizeError } from '@/lib/errors';
import { AiSkillsCard } from './marathon/AiSkillsCard';
import { ClassificationCard } from './marathon/ClassificationCard';
import { useCourseFeatureFlags } from './courseFeatureFlags';

/**
 * Настройки курса: возможности и дополнительные курсы.
 *
 * ⚠️ ЗАЧЕМ ОНИ ЗДЕСЬ. До 07.09.2026 и то и другое настраивали в тарифе —
 * отдельной сущности рядом с курсом. Школа мыслит курсами: «сентябрьский
 * pre-upper для групп» — один предмет разговора, а не два. Заказчик: «все
 * конфигурации, которые были в тарифе, теперь будут в настройках марафона».
 *
 * ⚠️ ТАРИФЫ НЕ УДАЛЕНЫ. Они остались в базе со всеми значениями; на сервере
 * есть возврат к ним одной переменной окружения. Здесь их просто больше не
 * настраивают.
 */
export function MarathonSettingsTab({ marathonId }: { marathonId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { featureFlags } = useCourseFeatureFlags();
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [extras, setExtras] = useState<Set<string>>(new Set());
  const [grading, setGrading] = useState<GradingMode>('auto');

  const settings = useQuery({
    queryKey: ['admin', 'marathon', marathonId, 'settings'],
    queryFn: () => coursesApi.getMarathonSettings(marathonId),
  });

  // Список курсов, из которых выбирают дополнительные.
  const all = useQuery({
    queryKey: ['admin', 'marathons', 'for-extras'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100 }),
  });

  useEffect(() => {
    if (!settings.data) return;
    setPicked(new Set(settings.data.feature_keys ?? []));
    setExtras(new Set((settings.data.extras ?? []).map((e) => e.marathon_id)));
    setGrading(settings.data.grading_mode ?? 'auto');
  }, [settings.data]);

  const saveRights = useMutation({
    mutationFn: () => coursesApi.setMarathonRights(marathonId, [...picked]),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'settings'] });
    },
    onError: (e) => setError(humanizeError(e)),
  });

  const saveExtras = useMutation({
    mutationFn: () => coursesApi.setMarathonExtras(marathonId, [...extras]),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'settings'] });
    },
    onError: (e) => setError(humanizeError(e)),
  });

  const saveGrading = useMutation({
    mutationFn: () => coursesApi.setMarathonGrading(marathonId, grading),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'settings'] });
    },
    onError: (e) => setError(humanizeError(e)),
  });

  const toggle = (set: Set<string>, id: string, apply: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
  };

  if (settings.isLoading) {
    return <p className="py-6 text-center text-sm text-on-surface-variant">{t('common.loading')}</p>;
  }

  const courses = (all.data?.items ?? []).filter((m: { id: string }) => m.id !== marathonId);

  return (
    <div className="space-y-4">
      {error && (
        <Card variant="subtle" className="text-sm text-error">
          {error}
        </Card>
      )}

      {/*
        ⚠️ ТУМБЛЕР НЕ ПОКАЗЫВАЕТСЯ У БЕСПЛАТНОГО КУРСА. Режим на мини-курсы не
        действует: балл там берётся из самого урока, как и до разбаловки —
        решение владельца «мини-курсы не трогать». Показать переключатель,
        который ничего не меняет, значит соврать администратору.
      */}
      <Card>
        <CardTitle>{t('marathonSettings.gradingTitle')}</CardTitle>
        <p className="mb-4 mt-1 text-[13px] text-on-surface-variant">
          {settings.data?.is_free
            ? t('marathonSettings.gradingFreeCourse')
            : t('marathonSettings.gradingHint')}
        </p>

        {!settings.data?.is_free && (
          <>
            <div className="grid gap-2">
              {(['auto', 'curator'] as GradingMode[]).map((mode) => (
                <label
                  key={mode}
                  className="flex cursor-pointer items-center gap-2.5 rounded-button bg-surface-container-low px-3.5 py-2.5"
                >
                  <input
                    type="radio"
                    name="grading-mode"
                    checked={grading === mode}
                    onChange={() => setGrading(mode)}
                    className="h-4 w-4 shrink-0 accent-primary"
                  />
                  <span className="text-[13.5px] text-on-surface">
                    {mode === 'auto'
                      ? t('marathonSettings.gradingAuto')
                      : t('marathonSettings.gradingCurator')}
                  </span>
                </label>
              ))}
            </div>

            {/* Смена режима не переписывает прошлое — решение владельца.
                Без этой подсказки администратор ждал бы пересчёта. */}
            <p className="mt-3 text-[12px] text-on-surface-variant">
              {t('marathonSettings.gradingNoticePast')}
            </p>

            <Button className="mt-4" onClick={() => saveGrading.mutate()} disabled={saveGrading.isPending}>
              {saveGrading.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </>
        )}
      </Card>

      {/* Подтип и категория доступа. Раньше подтип здесь только показывался
          пилюлей у «Возможностей курса» — поставить его было нельзя ниоткуда,
          хотя сервер умеет это с 11.09.2026 (договор `mirror-v2.md`). */}
      <ClassificationCard marathonId={marathonId} />

      {/* ИИ-задания курса — рядом с «Потоком и доступом» (docs/CONTRACTS/ai-skills.md §4). */}
      <AiSkillsCard marathonId={marathonId} />

      <Card>
        <CardTitle>{t('marathonSettings.rightsTitle')}</CardTitle>
        <p className="mb-4 mt-1 text-[13px] text-on-surface-variant">{t('marathonSettings.rightsHint')}</p>

        <div className="grid gap-2 sm:grid-cols-2">
          {featureFlags.map((f) => (
            <label
              key={f.id}
              className="flex cursor-pointer items-center gap-2.5 rounded-button bg-surface-container-low px-3.5 py-2.5"
            >
              <input
                type="checkbox"
                checked={picked.has(f.id)}
                onChange={() => toggle(picked, f.id, setPicked)}
                className="h-4 w-4 shrink-0 accent-primary"
              />
              <span className="text-[13.5px] text-on-surface">{f.label}</span>
            </label>
          ))}
        </div>

        <Button className="mt-4" onClick={() => saveRights.mutate()} disabled={saveRights.isPending}>
          {saveRights.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </Card>

      <Card>
        <CardTitle>{t('marathonSettings.extrasTitle')}</CardTitle>
        <p className="mb-4 mt-1 text-[13px] text-on-surface-variant">
          {t('marathonSettings.extrasHint')}
        </p>

        {/* grid-cols-1, а не неявный столбец: тот растягивался под самое длинное название курса,
            truncate не срабатывал, и на 390 вкладка уходила вбок на 97 px. */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {courses.map((m: { id: string; title: string }) => (
            <label
              key={m.id}
              className="flex cursor-pointer items-center gap-2.5 rounded-button bg-surface-container-low px-3.5 py-2.5"
            >
              <input
                type="checkbox"
                checked={extras.has(m.id)}
                onChange={() => toggle(extras, m.id, setExtras)}
                className="h-4 w-4 shrink-0 accent-primary"
              />
              <span className="truncate text-[13.5px] text-on-surface">{m.title}</span>
            </label>
          ))}
        </div>

        <Button className="mt-4" onClick={() => saveExtras.mutate()} disabled={saveExtras.isPending}>
          {saveExtras.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </Card>
    </div>
  );
}
